import type { ZodValidator } from '@tanstack/zod-form-adapter';

import { api } from '@connected/api-client';
import { validators } from '@connected/api-client/generated/validators.generated';
import { usePatcher } from '@connected/api-client/hooks/use-patcher';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { CurrencyField } from '@connected/form-fields/components/currency';
import { DateField } from '@connected/form-fields/components/date';
import FieldInfo from '@connected/form-fields/components/field-info';
import { Label } from '@connected/form-fields/components/label';
import { NumberField } from '@connected/form-fields/components/number';
import {
    MultipleOptionField,
    MultipleOptionItem,
    SingleCheckOptionItem,
    SingleOptionField,
} from '@connected/form-fields/components/option';
import { Button } from '@connected/ui/components/button';
import { Input, InputGroupControl } from '@connected/ui/components/input';
import { InputGroup } from '@connected/ui/components/input-group';
import { Section } from '@connected/ui/features/layouts';
import { Container } from '@connected/ui/layouts/container';
import { useForm } from '@tanstack/react-form';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import type { Option } from '@/hooks/use-option';

import { ElseField } from '@/components/else-field';
import { FooterNavigation } from '@/components/navigation/footer-navigation';
import { useCondition } from '@/config/conditions';
import { useSelectableOptions } from '@/hooks/use-option';
import { queryClient } from '@/lib/query-client';
import { useOffline } from '@/stores/network-status';

export const Route = createFileRoute('/(auth)/projects/$projectVersionId/quotation/')({
    component: Page,
    loader: ({ context, params }) => {
        context.queryClient.ensureQueryData(elektraAppQueryOptions.project.request.objectOptions({ electricalProjectVersionId: params.projectVersionId }));
        context.queryClient.ensureQueryData(elektraAppQueryOptions.project.inspectionPlan.inspectionPlanEditOptions({ electricalProjectVersionId: params.projectVersionId }));
        context.queryClient.ensureQueryData(elektraAppQueryOptions.project.quotation.quotationOptions({ electricalProjectVersionId: params.projectVersionId }));
        context.queryClient.ensureQueryData(elektraAppQueryOptions.project.selectListsItems.selectListsItemsGetOptions({ electricalProjectVersionId: params.projectVersionId, listKind: 'AdditionalInstruction' }));
        context.queryClient.ensureQueryData(elektraAppQueryOptions.project.schedule.projectUsersListOptions({
            electricalProjectVersionId: params.projectVersionId,
            userKind: 'PriceQuoteSigner',
        }));
    },
});

type TariffFormValues = {
    basePrice: number | null;
    squareMeterCount: number | null;
    pricePerSquareMeter: number | null;
    distributionBoardCount: number | null;
    pricePerDistributionBoard: number | null;
    controlBoxCount: number | null;
    pricePerControlBox: number | null;
    pricePerRepeatInspection: number | null;
    pricePerRemoteRepeatInspection: number | null;
    discountPercentage: number | null;
    pvInverterCount: number | null;
    pricePerPVInverter: number | null;
    pvPanelCount: number | null;
    pricePerPVPanel: number | null;
};

function Page() {
    const { t } = useTranslation('quotes');
    return (
        <div className="flex flex-col gap-4">
            <Container>
                <Container.Header>
                    <Container.Title>{t('layout.title')}</Container.Title>
                </Container.Header>
                <Container.Content>
                    <InformationForm />
                </Container.Content>
            </Container>
            <Container>
                <Container.Header>
                    <Container.Title>{t('layout.tariffs')}</Container.Title>
                </Container.Header>
                <Container.Content>
                    <PricesForm />
                </Container.Content>
                <Section.Footer>
                    <FooterNavigation />
                </Section.Footer>
            </Container>
        </div>
    );
}

type InspectionPlanFormValues = {
    sampleMethodKind: string;
    sampleMethodOther: string;
    priceQuoteNumber: string;
};

type QuotationFormValues = {
    priceQuoteDate: Date;
};

function InformationForm() {
    const { t } = useTranslation('quotes');

    const params = Route.useParams();

    const isUserOffline = useOffline();

    const additionalInstructions = useQuery(elektraAppQueryOptions.project.selectListsItems.selectListsItemsGetOptions({ electricalProjectVersionId: params.projectVersionId, listKind: 'AdditionalInstruction' }));
    const inspectionPlan = useQuery(elektraAppQueryOptions.project.inspectionPlan.inspectionPlanEditOptions({ electricalProjectVersionId: params.projectVersionId }));
    const quotationData = useQuery(elektraAppQueryOptions.project.quotation.quotationOptions({ electricalProjectVersionId: params.projectVersionId }));
    const quotationUsers = useQuery(elektraAppQueryOptions.project.schedule.projectUsersListOptions({
        electricalProjectVersionId: params.projectVersionId,
        userKind: 'PriceQuoteSigner',
    }));

    const { Field, Subscribe, setFieldValue } = useForm<InspectionPlanFormValues, ZodValidator>({
        defaultValues: {
            sampleMethodKind:
                inspectionPlan?.data?.InspectionPlan?.SampleMethodKind || '',
            sampleMethodOther:
                inspectionPlan?.data?.InspectionPlan?.SampleMethodOther || '',
            priceQuoteNumber: inspectionPlan?.data?.InspectionPlan?.PriceQuoteNumber || '',
        }, // /api/v2/electrical/projects/inspectionplans/edit
        validatorAdapter: zodValidator(),
    });

    const quotationForm = useForm<QuotationFormValues, ZodValidator>({
        defaultValues: {
            priceQuoteDate: quotationData?.data?.PriceQuote?.PriceQuoteDate
                ? new Date(quotationData?.data?.PriceQuote?.PriceQuoteDate)
                : new Date(),
        },
        validatorAdapter: zodValidator(),
    });

    const additionalInstructionsOptions = useSelectableOptions({
        options:
            additionalInstructions.data?.Items.map(item => ({
                value: item.ElectricalProjectSelectListItemId!,
                label: item.Label!,
            })) || ([] as Option<string>[]),
        initialSelectedValues:
            additionalInstructions.data?.Items.filter(x => x.Selected).map(
                x => x.ElectricalProjectSelectListItemId!,
            ) ?? [],
        selectItemType: 'AdditionalInstruction',
        electricalProjectVersionId: params.projectVersionId,
        parentKind: 'Quotation',
    });

    const selectedProjectUserMutation = api.useMutation(
        'put',
        '/api/v2/electrical/projects/users/edit',
        {
            onSettled: async (_data, _error, _variables) => {
                await queryClient.invalidateQueries(elektraAppQueryOptions.project.schedule.projectUsersListOptions({
                    electricalProjectVersionId: params.projectVersionId,
                    userKind: 'PriceQuoteSigner',
                }));
            },
            scope: {
                id: params.projectVersionId,
            },
        },
    );

    async function handleUserChange(userId: string) {
        await selectedProjectUserMutation.mutateAsync({
            body: {
                ElectricalProjectVersionId: params.projectVersionId,
                UserId: userId,
                Selected: true,
                Kind: 'PriceQuoteSigner',
            },
        });
    }

    // const patcher = createPatcher(
    //     '/api/v2/electrical/projects/inspectionplans/patch',
    //     {
    //         electricalProjectVersionId: search.electricalProjectVersionId,
    //     },
    // );

    const { patchField } = usePatcher('/api/v2/electrical/projects/inspectionplans/patch', {
        onSettled: () => {
            queryClient.invalidateQueries(
                elektraAppQueryOptions.project.inspectionPlan.inspectionPlanEditOptions({ electricalProjectVersionId: params.projectVersionId }),
            );
            queryClient.invalidateQueries(
                elektraAppQueryOptions.project.request.objectOptions({ electricalProjectVersionId: params.projectVersionId }),
            );
        },
    }, {
        body: {
            ElectricalProjectVersionId: params.projectVersionId,
        },
    });

    return (
        <div className="flex flex-col gap-4">
            <Field
                name="priceQuoteNumber"
                validators={{
                    onChange:
                        validators.editElectricalProjectInspectionPlanCommand.priceQuoteNumber,
                    onChangeAsync: patchField('PriceQuoteNumber'),
                    onChangeAsyncDebounceMs: patchField.debounceTime,
                }}
            >
                {({ handleChange, state, name }) => (
                    <Label label={t('information.form.quoteNumber.label')} className="w-full">
                        <Input
                            name={name}
                            placeholder={t('information.form.quoteNumber.placeholder')}
                            className="w-full flex flex-row"
                            value={state.value}
                            onChange={e => handleChange(e.target.value)}
                            disabled={isUserOffline}
                        />
                        <FieldInfo state={state} />
                    </Label>
                )}
            </Field>
            <Label label={t('information.form.additionalTopics.label')}>
                <MultipleOptionField
                    handleChange={additionalInstructionsOptions.onChange}
                    state={{
                        value: additionalInstructionsOptions?.selectedValues,
                    }}
                    options={additionalInstructionsOptions?.options || []}
                    render={MultipleOptionItem}
                    dropdown
                    disabled={isUserOffline}
                    emptyOptionsMessage={t('information.form.additionalTopics.emptyMessage')}
                    placeholder={t('information.form.additionalTopics.placeholder')}
                    search
                />
            </Label>
            <Field
                name="sampleMethodKind"
                validators={{
                    onChange:
                        validators.editElectricalProjectInspectionPlanCommand.sampleMethodKind,
                    onChangeAsync: async (value) => {
                        const response = await patchField(
                            'SampleMethodKind',
                        )(value);

                        if (value?.value !== 'Other') {
                            setFieldValue(
                                'sampleMethodOther',
                                '',
                            );
                        }
                        return response;
                    },
                    onChangeAsyncDebounceMs: patchField.debounceTime,
                }}
            >
                {({ handleChange, state, name }) => (
                    <Label label={t('information.form.sampleMethod.label')}>
                        <SingleOptionField
                            name={name}
                            handleChange={handleChange}
                            state={state}
                            options={[
                                {
                                    label: 'Indien mogelijk steekproef conform NEN3140 bijlage J',
                                    value: 'ConformNen3140AppendixJ',
                                },
                                {
                                    label: 'Indien mogelijk steekproef conform NTA 8220 tabel 1',
                                    value: 'ConformNTA8220Tabel1',
                                },
                                {
                                    label: 'Zie aanvullende instructie',
                                    value: 'AdditionalInstruction',
                                },
                                {
                                    label: 'De steekproef beperkt zich tot de 1e steekproefcyclus',
                                    value: 'LimitToFirstSampleRound',
                                },
                                {
                                    label: 'De steekproef kan conform bijlage J worden uitgebreid',
                                    value: 'SampleCanBeExpandedBasedOnAppendixJ',
                                },
                                {
                                    label: '100%',
                                    value: 'Complete',
                                },
                                {
                                    label: 'Anders',
                                    value: 'Other',
                                },
                            ]}
                            render={SingleCheckOptionItem}
                            dropdown
                            closeOnClick
                            disabled={isUserOffline}
                            emptyOptionsMessage={t('information.form.sampleMethod.emptyMessage')}
                            placeholder={t('information.form.sampleMethod.placeholder')}
                            search
                        />
                        <FieldInfo state={state} />
                    </Label>
                )}
            </Field>

            <Subscribe selector={state => [state.values.sampleMethodKind]}>
                {([sampleMethodKind]) =>
                    sampleMethodKind === 'Other' && (
                        <Field
                            name="sampleMethodOther"
                            validators={{
                                onChange:
                                    validators.editElectricalProjectInspectionPlanCommand.sampleMethodOther,
                                onChangeAsync: patchField('sampleMethodOther'),
                                onChangeAsyncDebounceMs: patchField.debounceTime,
                            }}
                        >
                            {({ handleChange, state, name }) => (
                                <ElseField>
                                    <Label label={t('information.form.sampleMethod.label')} className="w-full">
                                        <Input
                                            name={name}
                                            placeholder={t('information.form.sampleMethod.otherPlaceholder')}
                                            className="w-full flex flex-row"
                                            value={state.value}
                                            onChange={e => handleChange(e.target.value)}
                                            disabled={isUserOffline}
                                        />
                                        <FieldInfo state={state} />
                                    </Label>
                                </ElseField>
                            )}
                        </Field>
                    )}
            </Subscribe>

            <Label
                label={t('information.form.quoteSigner.label')}
                className="w-full"
                htmlFor=""
            >
                <SingleOptionField
                    name="certifiedInspector"
                    handleChange={handleUserChange}
                    state={{ value: quotationUsers.data?.Users.find(user => user.Selected)?.UserId ?? '' }}
                    className="flex flex-row items-center justify-between"
                    options={quotationUsers.data?.Users.map(user => ({
                        label: user.DisplayName!,
                        value: user.UserId!,
                    })) ?? []}
                    render={SingleCheckOptionItem}
                    dropdown
                    closeOnClick
                    emptyOptionsMessage={t('information.form.quoteSigner.emptyMessage')}
                    placeholder={t('information.form.quoteSigner.placeholder')}
                />
            </Label>
            <quotationForm.Field
                name="priceQuoteDate"
                validators={{
                    onChange:
                        validators.patchElectricalProjectPriceQuoteCommand.priceQuoteDate,
                    onChangeAsync: patchField(
                        'priceQuoteDate',
                        (value: unknown) =>
                            new Date(
                                value as string,
                            ).toDateString(),
                    ),
                    onChangeAsyncDebounceMs: patchField.debounceTime,
                }}
            >
                {({ handleChange, state }) => (
                    <Label label="Offerte datum" className="w-full">
                        <DateField
                            value={state.value}
                            onChange={(values) => {
                                handleChange(values);
                            }}
                            disabled={isUserOffline || { before: new Date() }}
                        />
                        <FieldInfo state={state} />
                    </Label>
                )}
            </quotationForm.Field>
        </div>
    );
}

function PricesForm() {
    const { t } = useTranslation('quotes');

    const canAccessSolarInspection = useCondition('canAccessSolarInspection');

    const isUserOffline = useOffline();

    const params = Route.useParams();

    const objectData = useQuery(elektraAppQueryOptions.project.request.objectOptions({ electricalProjectVersionId: params.projectVersionId }))?.data;
    const quotationData = useQuery(elektraAppQueryOptions.project.quotation.quotationOptions({ electricalProjectVersionId: params.projectVersionId }))?.data;

    const projectObject = objectData?.ProjectObject;
    const priceQuote = quotationData?.PriceQuote;

    const quotationDataMutation = api.useMutation(
        'put',
        '/api/v2/electrical/projects/pricequotes/edit',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(elektraAppQueryOptions.project.request.objectOptions({ electricalProjectVersionId: params.projectVersionId }));
            },
        },
    );

    const createDocumentMutation = api.useMutation(
        'post',
        '/api/v2/electrical/projects/documents/create',
        {
            onSuccess: () => {
                toast.success('Document wordt aan de wachtrij toegevoegd, je kunt deze later terugvinden onder documenten');
            },
        },
    );

    const { patchField } = usePatcher('/api/v2/electrical/projects/pricequotes/patch', {
        onSettled: () => {
            queryClient.invalidateQueries(elektraAppQueryOptions.project.request.objectOptions({ electricalProjectVersionId: params.projectVersionId }));
            queryClient.invalidateQueries(elektraAppQueryOptions.project.quotation.quotationOptions({ electricalProjectVersionId: params.projectVersionId }));
        },
    }, {
        body: {
            ElectricalProjectVersionId: params.projectVersionId,
        },
    });

    const { Field, handleSubmit, Subscribe } = useForm<
        TariffFormValues,
        ZodValidator
    >({
        defaultValues: {
            basePrice: priceQuote?.BasePrice || null,
            squareMeterCount:
                priceQuote?.SquareMeterCount
                ?? (projectObject?.TotalFloorArea
                    || null),
            pricePerSquareMeter: priceQuote?.PricePerSquareMeter || null,
            distributionBoardCount:
                priceQuote?.DistributionBoardCount
                ?? (projectObject?.DistributionBoardCount
                    || null),
            pricePerDistributionBoard:
                priceQuote?.PricePerDistributionBoard || null,
            controlBoxCount:
                priceQuote?.ControlBoxCount
                ?? (projectObject?.ControlBoxCount
                    || null),
            pricePerControlBox: priceQuote?.PricePerControlBox || null,
            pricePerRepeatInspection:
                priceQuote?.PricePerRepeatInspection || null,
            pricePerRemoteRepeatInspection:
                priceQuote?.PricePerRemoteRepeatInspection || null,
            discountPercentage: priceQuote?.DiscountPercentage || null,
            pvInverterCount: priceQuote?.PVInverterCount ?? (projectObject?.PVInverterCount || null),
            pricePerPVInverter: priceQuote?.PricePerPVInverter || null,
            pvPanelCount: priceQuote?.PVPanelCount ?? (projectObject?.PVPanelCount || null),
            pricePerPVPanel: priceQuote?.PricePerPVPanel || null,

        },
        onSubmit: async ({ value }) => {
            return quotationDataMutation.mutateAsync({
                body: {
                    ElectricalProjectVersionId: params.projectVersionId,
                    BasePrice: value.basePrice,
                    SquareMeterCount: value.squareMeterCount,
                    PricePerSquareMeter: value.pricePerSquareMeter,
                    DistributionBoardCount: value.distributionBoardCount,
                    PricePerDistributionBoard: value.pricePerDistributionBoard,
                    ControlBoxCount: value.controlBoxCount,
                    PricePerControlBox: value.pricePerControlBox,
                    PricePerRepeatInspection: value.pricePerRepeatInspection,
                    DiscountPercentage: value.discountPercentage,
                    PVInverterCount: value.pvInverterCount,
                    PricePerPVInverter: value.pricePerPVInverter,
                    PVPanelCount: value.pvPanelCount,
                    PricePerPVPanel: value.pricePerPVPanel,
                },
            });
        },
        validatorAdapter: zodValidator(),
    });

    async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        await handleSubmit();
    }

    return (
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <div className="grid grid-cols-1 items-center sm:grid-cols-2 md:grid-cols-4  bg-background-secondary p-4 rounded-md ">
                <p className="col-span-1 md:col-span-3 text-text-primary text-sm font-bold">
                    {t('tariffs.form.startTariff.label')}
                </p>

                <Field
                    name="basePrice"
                    validators={{
                        onChange:
                            validators.patchElectricalProjectPriceQuoteCommand.basePrice,
                        onChangeAsync: patchField('BasePrice'),
                        onChangeAsyncDebounceMs: patchField.debounceTime,
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <Label
                            label={t('tariffs.form.tariff.label')}
                            className="w-full sm:w-auto md:pl-2"
                        >
                            <CurrencyField
                                name={name}
                                value={state.value}
                                currency="EUR"
                                onChange={handleChange}
                                min={0}
                                disabled={isUserOffline}
                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </Field>
            </div>

            <div className="grid grid-cols-1 items-center sm:grid-cols-2  bg-background-secondary p-4 rounded-md">
                <p className="text-text-primary text-sm font-bold">{t('tariffs.form.tariffPerSquareMeter.label')}</p>
                {' '}
                {/* TODO: tooltip */}
                <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2">
                    <Field
                        name="squareMeterCount"
                        validators={{
                            onChange:
                                validators.editElectricalProjectPriceQuoteCommand.squareMeterCount,
                            onChangeAsync: patchField('SquareMeterCount'),
                            onChangeAsyncDebounceMs: patchField.debounceTime,
                        }}
                    >
                        {({ handleChange, state, name }) => (
                            <Label
                                label="m&sup2;"
                                className="w-full sm:w-auto"
                                htmlFor={name}
                            >
                                <NumberField
                                    name={name}
                                    value={state.value}
                                    onChange={handleChange}
                                    min={0}
                                    disabled={isUserOffline}
                                />
                                <FieldInfo state={state} />
                            </Label>
                        )}
                    </Field>
                    <Field
                        name="pricePerSquareMeter"
                        validators={{
                            onChange:
                                validators.editElectricalProjectPriceQuoteCommand.pricePerSquareMeter,
                            onChangeAsync: patchField('PricePerSquareMeter'),
                            onChangeAsyncDebounceMs: patchField.debounceTime,
                        }}
                    >
                        {({ handleChange, state, name }) => (
                            <Label
                                label={t('tariffs.form.tariff.label')}
                                className="w-full sm:w-auto"
                            >
                                <CurrencyField
                                    name={name}
                                    value={state.value}
                                    currency="EUR"
                                    onChange={handleChange}
                                    min={0}
                                    disabled={isUserOffline}
                                />
                                <FieldInfo state={state} />
                            </Label>
                        )}
                    </Field>
                </div>
            </div>

            <div className="grid grid-cols-1 items-center sm:grid-cols-2  bg-background-secondary p-4 rounded-md">
                <p className="text-text-primary text-sm font-bold">{t('tariffs.form.tariffPerDistributionBox.label')}</p>
                <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2">
                    <Field
                        name="distributionBoardCount"
                        validators={{
                            onChange:
                                validators.editElectricalProjectPriceQuoteCommand.distributionBoardCount,
                            onChangeAsync: patchField('DistributionBoardCount'),
                            onChangeAsyncDebounceMs: patchField.debounceTime,
                        }}
                    >
                        {({ handleChange, state, name }) => (
                            <Label
                                label={t('tariffs.form.amount.label')}
                                className="w-full sm:w-auto"
                                htmlFor={name}
                            >

                                <NumberField
                                    name={name}
                                    value={state.value}
                                    onChange={handleChange}
                                    min={0}
                                    disabled={isUserOffline}
                                />

                                <FieldInfo state={state} />
                            </Label>
                        )}
                    </Field>
                    <Field
                        name="pricePerDistributionBoard"
                        validators={{
                            onChange:
                                validators.editElectricalProjectPriceQuoteCommand.pricePerDistributionBoard,
                            onChangeAsync: patchField('PricePerDistributionBoard'),
                            onChangeAsyncDebounceMs: patchField.debounceTime,
                        }}
                    >
                        {({ handleChange, state, name }) => (
                            <Label
                                label={t('tariffs.form.tariff.label')}
                                className="w-full sm:w-auto"
                            >
                                <CurrencyField
                                    name={name}
                                    value={state.value}
                                    currency="EUR"
                                    onChange={handleChange}
                                    disabled={isUserOffline}
                                />
                                <FieldInfo state={state} />
                            </Label>
                        )}
                    </Field>
                </div>
            </div>

            <div className="grid grid-cols-1 items-center sm:grid-cols-2  bg-background-secondary p-4 rounded-md">
                <p className="text-text-primary text-sm font-bold">{t('tariffs.form.tariffPerControlCabinet.label')}</p>
                <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2">
                    <Field
                        name="controlBoxCount"
                        validators={{
                            onChange:
                                validators.editElectricalProjectPriceQuoteCommand.controlBoxCount,
                            onChangeAsync: patchField('ControlBoxCount'),
                            onChangeAsyncDebounceMs: patchField.debounceTime,
                        }}
                    >
                        {({ handleChange, state, name }) => (
                            <Label
                                label={t('tariffs.form.amount.label')}
                                className="w-full sm:w-auto"
                                htmlFor={name}
                            >
                                <NumberField
                                    name={name}
                                    value={state.value}
                                    onChange={handleChange}
                                    min={0}
                                    disabled={isUserOffline}
                                />
                                <FieldInfo state={state} />
                            </Label>
                        )}
                    </Field>
                    <Field
                        name="pricePerControlBox"
                        validators={{
                            onChange:
                                validators.editElectricalProjectPriceQuoteCommand.pricePerControlBox,
                            onChangeAsync: patchField('PricePerControlBox'),
                            onChangeAsyncDebounceMs: patchField.debounceTime,
                        }}
                    >
                        {({ handleChange, state, name }) => (
                            <Label
                                label={t('tariffs.form.tariff.label')}
                                className="w-full sm:w-auto"
                            >
                                <CurrencyField
                                    name={name}
                                    value={state.value}
                                    currency="EUR"
                                    onChange={handleChange}
                                    min={0}
                                    disabled={isUserOffline}
                                />
                                <FieldInfo state={state} />
                            </Label>
                        )}
                    </Field>
                </div>
            </div>

            {canAccessSolarInspection && (
                <>

                    <div className="grid grid-cols-1 items-center sm:grid-cols-2  bg-background-secondary p-4 rounded-md">
                        <p className="text-text-primary text-sm font-bold">{t('tariffs.form.tariffPerInverter.label')}</p>
                        <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2">
                            <Field
                                name="pvInverterCount"
                                validators={{
                                    onChange:
                                        validators.editElectricalProjectPriceQuoteCommand.pVInverterCount,
                                    onChangeAsync: patchField('PVInverterCount'),
                                    onChangeAsyncDebounceMs: patchField.debounceTime,
                                }}
                            >
                                {({ handleChange, state, name }) => (
                                    <Label
                                        label={t('tariffs.form.amount.label')}
                                        className="w-full sm:w-auto"
                                        htmlFor={name}
                                    >
                                        <NumberField
                                            name={name}
                                            value={state.value}
                                            onChange={handleChange}
                                            min={0}
                                            disabled={isUserOffline}
                                        />
                                        <FieldInfo state={state} />
                                    </Label>
                                )}
                            </Field>
                            <Field
                                name="pricePerPVInverter"
                                validators={{
                                    onChange:
                                        validators.editElectricalProjectPriceQuoteCommand.pricePerPVInverter,
                                    onChangeAsync: patchField('pricePerPVInverter'),
                                    onChangeAsyncDebounceMs: patchField.debounceTime,
                                }}
                            >
                                {({ handleChange, state, name }) => (
                                    <Label
                                        label={t('tariffs.form.tariff.label')}
                                        className="w-full sm:w-auto"
                                    >
                                        <CurrencyField
                                            name={name}
                                            value={state.value}
                                            currency="EUR"
                                            onChange={handleChange}
                                            min={0}
                                            disabled={isUserOffline}
                                        />
                                        <FieldInfo state={state} />
                                    </Label>
                                )}
                            </Field>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 items-center sm:grid-cols-2  bg-background-secondary p-4 rounded-md">
                        <p className="text-text-primary text-sm font-bold">{t('tariffs.form.pvPanelCount.label')}</p>
                        <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2">
                            <Field
                                name="pvPanelCount"
                                validators={{
                                    onChange:
                                        validators.editElectricalProjectPriceQuoteCommand.pVPanelCount,
                                    onChangeAsync: patchField('pvPanelCount'),
                                    onChangeAsyncDebounceMs: patchField.debounceTime,
                                }}
                            >
                                {({ handleChange, state, name }) => (
                                    <Label
                                        label={t('tariffs.form.amount.label')}
                                        className="w-full sm:w-auto"
                                        htmlFor={name}
                                    >
                                        <NumberField
                                            name={name}
                                            value={state.value}
                                            onChange={handleChange}
                                            min={0}
                                            disabled={isUserOffline}
                                        />
                                        <FieldInfo state={state} />
                                    </Label>
                                )}
                            </Field>
                            <Field
                                name="pricePerPVPanel"
                                validators={{
                                    onChange:
                                        validators.editElectricalProjectPriceQuoteCommand.pricePerPVPanel,
                                    onChangeAsync: patchField('pricePerPVPanel'),
                                    onChangeAsyncDebounceMs: patchField.debounceTime,
                                }}
                            >
                                {({ handleChange, state, name }) => (
                                    <Label
                                        label={t('tariffs.form.tariff.label')}
                                        className="w-full sm:w-auto"
                                    >
                                        <CurrencyField
                                            name={name}
                                            value={state.value}
                                            currency="EUR"
                                            onChange={handleChange}
                                            min={0}
                                            disabled={isUserOffline}
                                        />
                                        <FieldInfo state={state} />
                                    </Label>
                                )}
                            </Field>
                        </div>
                    </div>
                </>
            )}

            <div className="grid grid-cols-1 items-center sm:grid-cols-2 md:grid-cols-4  bg-background-secondary p-4 rounded-md">
                <p className="col-span-1 md:col-span-3 text-text-primary text-sm font-bold">
                    {t('tariffs.form.discountPercentage.label')}
                </p>

                <Field
                    name="discountPercentage"
                    validators={{
                        onChange:
                            validators.editElectricalProjectPriceQuoteCommand.discountPercentage,
                        onChangeAsync: patchField('DiscountPercentage'),
                        onChangeAsyncDebounceMs: patchField.debounceTime,
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <Label label="" className="w-full sm:w-auto md:pl-2">
                            <InputGroup>
                                <InputGroup.Addon>%</InputGroup.Addon>
                                <NumberField
                                    data-slot="input-group-control"
                                    customInput={InputGroupControl}
                                    className="flex-1 !border-none !shadow-none !ring-0 !outline-none focus:!border-none focus:!shadow-none focus:!ring-0 focus-visible:!ring-0 hover:!border-none hover:!shadow-none"
                                    name={name}
                                    onChange={handleChange}
                                />
                            </InputGroup>
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </Field>
            </div>
            <div className="flex flex-col items-center justify-center gap-4">
                <Subscribe selector={({ values }) => [values]}>
                    {([values]) => <Price values={values} />}
                </Subscribe>
                <Button
                    onClick={() => {
                        toast.info('Document wordt aangemaakt');
                        createDocumentMutation.mutate({
                            body: {
                                ElectricalProjectVersionId: params.projectVersionId,
                                Kind: 'PriceQuote',
                            },
                        });
                    }}
                    disabled={createDocumentMutation.isPending}
                >
                    Offerte genereren
                </Button>
            </div>
            <div className="grid grid-cols-1 items-center sm:grid-cols-2 md:grid-cols-4  bg-background-secondary p-4 rounded-md">
                <p className="col-span-1 md:col-span-3 text-text-primary text-sm font-bold">
                    {t('tariffs.form.tariffPerReInspection.label')}
                </p>

                <Field
                    name="pricePerRepeatInspection"
                    validators={{
                        onChange: validators.editElectricalProjectPriceQuoteCommand.pricePerRepeatInspection,
                        onChangeAsync: patchField('PricePerRepeatInspection'),
                        onChangeAsyncDebounceMs: patchField.debounceTime,
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <Label
                            label={t('tariffs.form.tariff.label')}
                            className="w-full sm:w-auto md:pl-2"
                        >
                            <CurrencyField
                                name={name}
                                value={state.value}
                                currency="EUR"
                                onChange={handleChange}
                                disabled={isUserOffline}
                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </Field>
            </div>
            <div className="grid grid-cols-1 items-center sm:grid-cols-2 md:grid-cols-4  bg-background-secondary p-4 rounded-md">
                <p className="col-span-1 md:col-span-3 text-text-primary text-sm font-bold">
                    {t('tariffs.form.tariffPerReInspectionRemote.label')}
                </p>

                <Field
                    name="pricePerRemoteRepeatInspection"
                    validators={{
                        onChange: validators.editElectricalProjectPriceQuoteCommand.pricePerRemoteRepeatInspection,
                        onChangeAsync: patchField('PricePerRemoteRepeatInspection'),
                        onChangeAsyncDebounceMs: patchField.debounceTime,
                    }}
                >
                    {({ handleChange, state, name }) => (
                        <Label
                            label={t('tariffs.form.tariff.label')}
                            className="w-full sm:w-auto md:pl-2"
                        >
                            <CurrencyField
                                name={name}
                                value={state.value}
                                currency="EUR"
                                onChange={handleChange}
                                disabled={isUserOffline}
                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </Field>
            </div>

        </form>
    );
}

function returnPrices<T extends Record<string, number | null>>(
    values: T,
): Record<keyof T, number> {
    const prices = Object.entries(values).reduce(
        (acc, [key, value]) => ({
            ...acc,
            [key]: value || 0,
        }),
        {},
    );

    return prices as Record<keyof T, number>;
}

function Price({ values }: { values: TariffFormValues }) {
    const { t } = useTranslation('quotes');

    const canAccessSolarInspection = useCondition('canAccessSolarInspection');
    const {
        basePrice,
        squareMeterCount,
        pricePerSquareMeter,
        distributionBoardCount,
        pricePerDistributionBoard,
        controlBoxCount,
        pricePerControlBox,
        discountPercentage,
        pvInverterCount,
        pricePerPVInverter,
        pvPanelCount,
        pricePerPVPanel,
    } = returnPrices(values as unknown as Record<string, number | null>);

    const total = useMemo(() => {
        const total
            = basePrice
                + (pricePerSquareMeter || 0) * (squareMeterCount || 0)
                + (pricePerDistributionBoard || 0) * (distributionBoardCount || 0)
                + (pricePerControlBox || 0) * (controlBoxCount || 0)
                + (canAccessSolarInspection
                    ? (pricePerPVInverter || 0) * (pvInverterCount || 0)
                    : 0)
                + (canAccessSolarInspection
                    ? (pricePerPVPanel || 0) * (pvPanelCount || 0)
                    : 0);
        return total - (total * (discountPercentage || 0)) / 100;
    }, [basePrice, pricePerSquareMeter, squareMeterCount, pricePerDistributionBoard, distributionBoardCount, pricePerControlBox, controlBoxCount, canAccessSolarInspection, pricePerPVInverter, pvInverterCount, pricePerPVPanel, pvPanelCount, discountPercentage]);

    return (
        <p className="font-semibold text-text-primary text-sm">
            {t('tariffs.form.totalTariff.label')}
            {' '}
            &euro;
            {(Math.round(total * 100) / 100).toFixed(2)}
        </p>
    );
}

function Footer() {
    const { t } = useTranslation('common');

    const params = Route.useParams();
    const search = Route.useSearch();

    return (
        <footer className="flex justify-between gap-4">
            <Button variant="ghost" asChild>
                <Link
                    to="/projects/$projectVersionId/applications/recap"
                    params={params}
                    search={search}
                >
                    {t('actions.navigation.previous')}
                </Link>
            </Button>
            <Button asChild variant="ghost">
                <Link
                    to="/projects/$projectVersionId/inspection-plans"
                    params={params}
                    search={search}
                >
                    {t('actions.navigation.next')}
                </Link>
            </Button>
        </footer>
    );
}
